import { useMarkSeen } from "../../lib/api/seen";
import { UniqueCode } from "../../components/accreditation/unique-code";
import {
	type AcCardTransitionAction,
	type AccreditationActions,
	type AccreditationRecord,
	REGISTRATION_NO_FALLBACK_ORG,
	type TravelDirection,
	allowedTransitions,
	transitionNeedsConfirm,
} from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AccessSheet } from "../../components/accreditation/access-sheet";
import { ActivityCount, ActivityList } from "../../components/accreditation/activity";
import { EditLink } from "../../components/accreditation/edit-link";
import { AlertTriangleIcon, CheckIcon, ChevronDownIcon, LockIcon, PencilIcon, TrashIcon, ZapIcon } from "../../components/accreditation/icons";
import { JourneyTracker } from "../../components/accreditation/journey";
import { actorSides, hiddenActionsFor } from "../../components/accreditation/model";
import { CategoryChip, MulticardTag, PhotoPlaceholder, StatusDot, UrgentTag } from "../../components/accreditation/parts";
import { RecordSections } from "../../components/accreditation/record-sections";
import { SetStatusModal } from "../../components/accreditation/set-status-modal";
import { StatusSheet, type StatusSheetTarget, refusalLabel } from "../../components/accreditation/status-sheet";
import { travelRefetchInterval } from "../../components/accreditation/travel-section";
import { TravelSheet } from "../../components/accreditation/travel-sheet";
import { useGamesTimeZone } from "../../components/travel-leg-form";
import { ConfirmWithPasswordModal } from "../../components/common/confirm-with-password-modal";
import { usePageBar } from "../../components/shell/page-bar";
import cornerUrl from "../../assets/brand/login-corner.svg";
import {
	accreditationActionsOptions,
	accreditationRecordOptions,
	bulkAccreditationUrgent,
	deleteAccreditation,
	invalidateAccreditations,
	transitionAccreditation,
} from "../../lib/api/accreditations";
import { canManageAccommodation, canSeeHostAccommodation } from "../../lib/accommodation/access";
import { ApiError } from "../../lib/api/client";
import { invalidateLogistics } from "../../lib/api/logistics-cache";
import { errorMessage } from "../../lib/api/messages";
import { gamesOptionsOptions } from "../../lib/competition/games";
import { nocModuleOpenFor } from "../../lib/noc-stages/access";
import { canDispatch } from "../../lib/transport/access";
import { toast } from "../../lib/ui/toast";

export const Route = createFileRoute("/_app/accreditation/records/$id")({
	component: RecordPage,
});

type LockReason = NonNullable<AccreditationActions["reasons"][string]>;

/**
 * One record (her `pgRecord`, admin.js:1027): banner with the numbers, the
 * category, the name and a status menu; the Returned / Rejected note; the
 * journey and the activity on the left; the sections on the right.
 *
 * What the viewer may do comes from `GET /accreditations/:id/actions` — the
 * same SQL the writes use. Every action is shown; the ones that are false
 * are drawn locked with the reason the API gave.
 */
/** The lists that badge a card "Updated" (records, rooming, journeys). */
const CARD_LISTS = [["accreditations"], ["accommodation"], ["travel"]] as const;

function RecordPage() {
	const { t } = useTranslation("accreditation");
	const { t: tc } = useTranslation("common");
	const { id } = Route.useParams();
	const { session } = Route.useRouteContext();
	const navigate = useNavigate();
	// M9: re-read while a leg's flight is still moving, so a delay shows without a reload.
	const record = useQuery({ ...accreditationRecordOptions(id), refetchInterval: (q) => travelRefetchInterval(q.state.data) });
	// Opening the record is "looking at it": the Updated marks on records, rooming and journeys clear.
	useMarkSeen("card", record.data ? id : null, CARD_LISTS);
	const actions = useQuery(accreditationActionsOptions(id));
	const sides = useMemo(() => actorSides(session), [session]);
	const hiddenActions = useMemo(() => hiddenActionsFor(session), [session]);
	// GMS-2: the logistics chain — who may arrange what (the API re-checks), and the NOC's sector gates.
	const chainAccess = useMemo(
		() => ({
			canArrange: canDispatch(session),
			canAssignRoom: canManageAccommodation(session),
			noc: session.active_role === "ORGANIZATION",
			showTransfer: nocModuleOpenFor(session, "transport"),
			showRoom: nocModuleOpenFor(session, "accommodation"),
		}),
		[session],
	);
	const games = useQuery(gamesOptionsOptions());
	const [statusTarget, setStatusTarget] = useState<{ target: StatusSheetTarget; action: AcCardTransitionAction } | null>(null);
	const [accessOpen, setAccessOpen] = useState(false);
	const [travelSheet, setTravelSheet] = useState<TravelDirection | null>(null);
	const travelTimeZone = useGamesTimeZone(record.data?.games_id);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [setStatusOpen, setSetStatusOpen] = useState(false);
	const [busy, setBusy] = useState(false);

	// Her `renderAbar('#/records', 'Record', regNo)`: title, the number under it, the back arrow — no in-page heading.
	usePageBar({
		title: t("record.title"),
		subtitle: record.data?.registration_no_display,
		back: { to: "/accreditation/records", search: true, label: t("record.back") },
	});

	if (record.isPending)
		return (
			<div className="flex flex-col gap-[1rem]">
				<div className="skeleton h-[11rem] rounded-[0.75rem]" />
				<div className="grid gap-[1rem] min-[64rem]:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
					<div className="skeleton h-[16rem] rounded-[0.625rem]" />
					<div className="skeleton h-[24rem] rounded-[0.625rem]" />
				</div>
			</div>
		);

	if (record.isError) {
		const notFound = record.error instanceof ApiError && (record.error.code === "NOT_FOUND" || record.error.code === "FORBIDDEN_SCOPE");
		return (
			<div className="flex flex-col gap-[1rem]">
				<div role="alert" className="grid justify-items-center gap-[0.5rem] rounded-[0.625rem] border border-gms-line bg-base-100 px-[1rem] py-[2.5rem] text-center">
					<b className="text-[0.9375rem] text-gms-navy-ink">{notFound ? t("record.notFound") : t("record.loadFailed")}</b>
					{!notFound && <p className="text-[0.8125rem] text-gms-ink-3">{errorMessage(record.error)}</p>}
					{!notFound && (
						<button type="button" className="btn btn-sm btn-outline mt-[0.25rem]" onClick={() => void record.refetch()}>
							{tc("dataTable.tryAgain")}
						</button>
					)}
				</div>
			</div>
		);
	}

	const r = record.data;
	const a = actions.data;
	const name = r.person.printed_name;
	const reasonText = (why: LockReason | undefined) => (why ? t(`record.lock.${why}`) : t("record.lock.status"));

	const runTransition = async (action: AcCardTransitionAction) => {
		// Every step but Submit asks first (2026-10-30); the remark / reason, when the edge needs one, is typed in that same dialog.
		if (transitionNeedsConfirm(action)) {
			setStatusTarget({
				action,
				target: { kind: "single", row: { ac_card_id: r.ac_card_id, status: r.status, printed_name: name, registration_no_display: r.registration_no_display } },
			});
			return;
		}
		setBusy(true);
		try {
			await transitionAccreditation(r.ac_card_id, action, {});
			// An approval moves the person into (or out of) the transport and accommodation lists.
			await invalidateLogistics();
			const to = allowedTransitions(r.status).find((e) => e.action === action)?.to;
			toast.success(t("record.transitionDone", { status: to ? t(`record.status.${to}`) : "" }));
		} catch (err) {
			toast.error(err, t("records.statusSheet.failed"));
		} finally {
			setBusy(false);
		}
	};

	const toggleUrgent = async () => {
		setBusy(true);
		try {
			const res = await bulkAccreditationUrgent(!r.urgent_printing, { ids: [r.ac_card_id] });
			await invalidateAccreditations();
			// A refusal is a failure, not a success (B3): nothing is shown as changed.
			const refusal = res.refused[0];
			if (res.updated === 0 && refusal) toast.error(new Error(t("record.urgentRefused", { reason: refusalLabel(t, refusal.reason) })), t("record.urgentFailed"));
			else toast.success(r.urgent_printing ? t("record.urgentUndone") : t("record.urgentDone"));
		} catch (err) {
			toast.error(err, t("record.urgentFailed"));
		} finally {
			setBusy(false);
		}
	};

	const isSuper = session.active_role === "SUPER_ADMIN";
	const canEdit = !!a?.edit;
	// The banner shows the display copy (960×1200), not the 160×200 list thumb.
	const bannerPhoto = r.files.find((f) => f.kind === "PHOTO")?.url ?? null;
	const showNote = (r.status === "RETURN" || r.status === "REJECTED") && r.status_note;

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			{/*
			  Her `.wl` band (admin.css:1386, v7): full-bleed on the light surface — it cancels the shell's <main>
			  padding — with the corner stripes and the red halftone in the top right. Not overflow-hidden: the
			  status menu must pop out; the art clips itself.
			*/}
			<section className="relative z-[5] -mx-[1rem] -mt-[1rem] border-b border-gms-line bg-[linear-gradient(180deg,#fff_0%,var(--color-gms-surface)_100%)] px-[1rem] pt-[1.125rem] pb-[1.375rem] md:-mx-[1.5rem] md:-mt-[1.5rem] md:px-[1.5rem] min-[45rem]:pt-[1.875rem] min-[45rem]:pb-[2.125rem] lg:-mx-[2rem] lg:-mt-[1.75rem] lg:px-[2rem]">
				<div aria-hidden className="pointer-events-none absolute inset-0 isolate overflow-hidden">
					<span className="absolute top-0 right-0 -z-10 h-[9.375rem] w-[46%] bg-[radial-gradient(var(--color-gms-nav-red)_1.5px,transparent_1.9px)] [background-size:9px_9px] opacity-85 [mask-image:radial-gradient(ellipse_100%_100%_at_100%_0%,#000_25%,rgba(0,0,0,0.55)_55%,transparent_85%)] min-[45rem]:h-[12.5rem] min-[45rem]:w-[42%] min-[64rem]:h-[15rem] min-[64rem]:w-[38%]" />
					<img
						src={cornerUrl}
						alt=""
						className="absolute top-[-3.125rem] right-[-6.875rem] w-[15rem] max-w-none opacity-90 [clip-path:inset(0_0_44%_0)] min-[45rem]:top-[-4.375rem] min-[45rem]:right-[-4.375rem] min-[45rem]:w-[20rem] min-[64rem]:top-[-6.875rem] min-[64rem]:right-[-2.5rem] min-[64rem]:w-[23.75rem]"
					/>
				</div>
				<div className="relative z-[1] grid gap-[1rem] min-[45rem]:max-w-[calc(100%-9.375rem)] min-[45rem]:grid-cols-[minmax(18.75rem,22.5rem)_minmax(0,1fr)] min-[45rem]:items-center min-[45rem]:gap-[1.75rem] min-[64rem]:max-w-[calc(100%-13.75rem)]">
					{/* .wl__l: 4:5 photo in a white frame, the two numbers, the category */}
					<div className="mx-auto grid w-full max-w-[26.25rem] grid-cols-[6.5rem_minmax(0,1fr)] grid-rows-[auto_auto_1fr] items-start gap-x-[0.875rem] gap-y-[0.5rem] min-[45rem]:mx-0 min-[45rem]:max-w-none min-[45rem]:grid-cols-[8.75rem_minmax(0,1fr)]">
						<div className="row-span-3">
							<PhotoPlaceholder name={name} size="hero" src={bannerPhoto} />
						</div>
						<dl className="grid content-start gap-[0.5rem]">
							<div>
								<dt className="text-[0.6875rem] font-semibold tracking-[0.05em] text-gms-ink-3 uppercase">{t("record.accreditationNo")}</dt>
								<dd className={`text-[0.9375rem] font-bold tracking-[0.02em] tabular-nums ${r.accreditation_no_display ? "text-gms-navy-ink" : "text-gms-ink-2"}`}>
									{r.accreditation_no_display ?? t("record.onApproval")}
								</dd>
							</div>
							<div>
								<dt className="text-[0.6875rem] font-semibold tracking-[0.05em] text-gms-ink-3 uppercase">{t("record.registrationNo")}</dt>
								<dd className="text-[0.9375rem] font-bold tracking-[0.02em] text-gms-navy-ink tabular-nums">{r.registration_no_display}</dd>
							</div>
							<UniqueCode value={r.unique_code} variant="record" />
						</dl>
						<div className="flex flex-wrap content-start items-center gap-[0.375rem]">
							<CategoryChip category={r.main_function} tone="solid" />
							{r.urgent_printing && <UrgentTag />}
							{r.multicard && <MulticardTag />}
						</div>
					</div>

					{/* .wl__r: the name, the organisation, the status menu, the action tiles */}
					<div className="grid min-w-0 gap-[0.875rem]">
						<div className="text-center min-[45rem]:text-left">
							<h2 className="font-display text-[1.625rem] leading-[0.95] font-bold break-words text-gms-navy uppercase min-[45rem]:text-[2.125rem]">{name}</h2>
							<p className="mt-[0.375rem] flex items-center justify-center gap-[0.375rem] text-[0.78125rem] text-gms-ink-2 min-[45rem]:justify-start">
								<span className="text-[0.6875rem] font-bold tracking-[0.04em] text-gms-ink-3">{r.responsible_organization.code ?? REGISTRATION_NO_FALLBACK_ORG}</span>
								{r.responsible_organization.name}
							</p>
							{/* On phones the menu anchors to this row (relative), not the button, so it stays on screen. */}
							<div className="relative mt-[0.75rem] inline-flex items-center gap-[0.5rem]">
								<span className="text-[0.75rem] font-semibold tracking-[0.06em] text-gms-ink-3 uppercase">{t("record.statusLabel")}</span>
								<StatusMenu
									record={r}
									actions={a}
									busy={busy}
									onPick={(act) => void runTransition(act)}
									onSetStatus={() => setSetStatusOpen(true)}
									reasonText={reasonText}
								/>
							</div>
						</div>
						{/* .qa2s: the action tiles — only what `/actions` allows is enabled; the rest drawn locked */}
						<div className="grid grid-cols-3 gap-[0.25rem] rounded-[0.625rem] bg-base-100 px-[0.375rem] py-[0.75rem] shadow-[0_14px_30px_-22px_rgba(9,10,40,0.45),0_0_0_1px_var(--color-gms-line)] min-[45rem]:w-fit min-[45rem]:grid-flow-col min-[45rem]:grid-cols-none min-[45rem]:auto-cols-[4.125rem] min-[45rem]:p-[0.625rem]">
							{canEdit ? (
								<EditLink acCardId={r.ac_card_id} className={qa}>
									<span className={qaIcon}>
										<PencilIcon />
									</span>
									{t("record.actions.edit")}
								</EditLink>
							) : (
								<QuickLocked label={t("record.actions.edit")} reason={a ? reasonText(a.reasons["edit"]) : undefined} icon={<PencilIcon />} />
							)}
							{/* Urgent printing is an edit: enabled only when `/actions` says `edit` (B3). */}
							{canEdit ? (
								<button type="button" className={qa} onClick={() => void toggleUrgent()} disabled={busy} aria-pressed={r.urgent_printing}>
									<span className={`${qaIcon} ${r.urgent_printing ? "bg-gms-bad-soft text-gms-bad" : ""}`}>
										<ZapIcon />
									</span>
									{r.urgent_printing ? t("record.actions.urgentOff") : t("record.actions.urgentOn")}
								</button>
							) : (
								<QuickLocked
									label={r.urgent_printing ? t("record.actions.urgentOff") : t("record.actions.urgentOn")}
									reason={a ? reasonText(a.reasons["edit"]) : undefined}
									icon={<ZapIcon />}
								/>
							)}
							{isSuper &&
								(a?.delete ? (
									<button type="button" className={qa} onClick={() => setDeleteOpen(true)} disabled={busy}>
										<span className={`${qaIcon} bg-gms-bad-soft text-gms-bad`}>
											<TrashIcon />
										</span>
										{t("record.actions.delete")}
									</button>
								) : (
									<QuickLocked label={t("record.actions.delete")} reason={a ? reasonText(a.reasons["delete"]) : undefined} icon={<TrashIcon />} danger />
								))}
						</div>
					</div>
				</div>
			</section>

			{showNote && (
				<div
					role="note"
					data-ac-status={r.status}
					className="flex gap-[0.625rem] rounded-[0.625rem] border border-[color-mix(in_srgb,var(--ac-dot)_35%,transparent)] bg-[var(--ac-bg)] px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-navy-ink"
				>
					<AlertTriangleIcon className="mt-[0.0625rem] size-[1rem] shrink-0 text-[var(--ac-ink)]" />
					<div className="grid gap-[0.125rem]">
						<b className="font-semibold">{r.status === "RETURN" ? t("record.note.RETURN") : t("record.note.REJECTED")}</b>
						<p className="whitespace-pre-line">{r.status_note}</p>
						{r.status === "RETURN" && canEdit && (
							<EditLink acCardId={r.ac_card_id} className="justify-self-start font-semibold text-gms-navy underline">
								{t("record.note.fixNow")}
							</EditLink>
						)}
					</div>
				</div>
			)}

			<div className="grid grid-cols-1 items-start gap-[0.75rem] min-[64rem]:mt-[0.25rem] min-[64rem]:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] min-[64rem]:gap-[1rem]">
				<div className="grid min-w-0 grid-cols-1 gap-[0.75rem] min-[64rem]:sticky min-[64rem]:top-[6.25rem]">
					<section aria-labelledby="ac-journey" className="grid min-w-0 grid-cols-1 gap-[0.5rem] pt-[0.25rem]">
						<h2 id="ac-journey" className="px-[0.25rem] text-[0.875rem] font-bold text-gms-navy-ink">
							{t("record.journey.title")}
						</h2>
						<div className="rounded-[0.625rem] border border-gms-line bg-base-100">
							<JourneyTracker
								status={r.status}
								journey={r.journey}
								validUntil={r.valid_until}
								fixLink={
									canEdit
										? (label) => (
												<EditLink acCardId={r.ac_card_id} className="hover:underline">
													{label}
												</EditLink>
											)
										: undefined
								}
							/>
						</div>
					</section>
					<section aria-labelledby="ac-activity" className="grid min-w-0 grid-cols-1 gap-[0.5rem] pt-[0.25rem]">
						<div className="flex items-baseline justify-between gap-[0.5rem] px-[0.25rem]">
							<h2 id="ac-activity" className="text-[0.875rem] font-bold text-gms-navy-ink">
								{t("record.activity.title")}
							</h2>
							<ActivityCount acCardId={r.ac_card_id} />
						</div>
						<ActivityList acCardId={r.ac_card_id} canInspect={isSuper} />
					</section>
				</div>

				<RecordSections
					record={r}
					gamesName={games.data?.find((g) => g.games_id === r.games_id)?.name}
					canEdit={canEdit}
					canEditAccess={!!a?.edit_access}
					onEditAccess={() => setAccessOpen(true)}
					canEditFiles={!!a?.files.card}
					travelTimeZone={travelTimeZone}
					canEditTravel={!!a?.edit_travel}
					onEditTravel={setTravelSheet}
					chainAccess={chainAccess}
					roomingOrgId={canSeeHostAccommodation(session) ? r.responsible_organization.id : undefined}
				/>
			</div>

			<StatusSheet
				hiddenActions={hiddenActions}
				open={statusTarget !== null}
				target={statusTarget?.target ?? null}
				initialAction={statusTarget?.action}
				sides={sides}
				onClose={() => setStatusTarget(null)}
			/>
			{a?.set_status && (
				<SetStatusModal
					open={setStatusOpen}
					onClose={() => setSetStatusOpen(false)}
					target={{ ac_card_id: r.ac_card_id, status: r.status, version: r.version, printed_name: name, registration_no_display: r.registration_no_display }}
				/>
			)}
			{a?.edit_access && <AccessSheet record={r} open={accessOpen} onClose={() => setAccessOpen(false)} />}
			{a?.edit_travel && <TravelSheet record={r} direction={travelSheet} timeZone={travelTimeZone} onClose={() => setTravelSheet(null)} />}
			{isSuper && (
				<ConfirmWithPasswordModal
					open={deleteOpen}
					onClose={() => setDeleteOpen(false)}
					title={t("record.delete.title")}
					description={
						<Trans
							t={t}
							i18nKey="record.delete.description"
							values={{ name, reg: r.registration_no_display }}
							components={{ strong: <strong className="font-semibold text-base-content" /> }}
						/>
					}
					confirmLabel={t("record.delete.confirm")}
					errorPrefix={t("record.delete.failed")}
					onConfirm={async (password) => {
						await deleteAccreditation(r.ac_card_id, password);
						await invalidateAccreditations();
						toast.success(t("record.delete.done", { reg: r.registration_no_display }));
						void navigate({ to: "/accreditation/records", search: (prev) => prev });
					}}
				/>
			)}
		</div>
	);
}

/* Her `.qa2` tile: 46px icon box, 22px glyph, 11.5px label; locked tiles at her .35. */
const qa =
	"grid min-w-0 cursor-pointer content-start justify-items-center gap-[0.375rem] rounded-[0.5rem] px-[0.125rem] py-[0.5rem] text-center text-[0.71875rem] leading-[1.2] font-medium text-gms-ink-2 outline-none hover:bg-gms-soft focus-visible:ring-2 focus-visible:ring-gms-navy/40 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent";
const qaIcon = "grid size-[2.875rem] place-items-center rounded-[0.5rem] bg-gms-navy-soft text-gms-navy [&>svg]:size-[1.375rem]";

/** A quick action the viewer cannot take: drawn, locked, with the API's reason as its description. */
function QuickLocked({ label, reason, icon, danger = false }: { label: string; reason?: string; icon: ReactNode; danger?: boolean }) {
	return (
		<button type="button" className={qa} disabled aria-disabled title={reason}>
			<span className={`${qaIcon} relative ${danger ? "bg-gms-bad-soft text-gms-bad" : ""}`}>
				{icon}
				<LockIcon className="absolute right-[0.1875rem] bottom-[0.1875rem] size-[0.625rem]" />
			</span>
			{label}
		</button>
	);
}

/**
 * Her status dropdown (`.sdd`, admin.js:1047) — but only the steps the graph
 * allows from this status, each enabled by `/actions` or drawn locked with
 * its reason. Steps that need a remark or a reason open the status sheet.
 */
function StatusMenu({
	record,
	actions,
	busy,
	onPick,
	onSetStatus,
	reasonText,
}: {
	record: AccreditationRecord;
	actions: AccreditationActions | undefined;
	busy: boolean;
	onPick: (action: AcCardTransitionAction) => void;
	/** The SUPER_ADMIN override, offered only when `/actions` says `set_status`. */
	onSetStatus: () => void;
	reasonText: (why: LockReason | undefined) => string;
}) {
	const { t } = useTranslation("accreditation");
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	const trigger = useRef<HTMLButtonElement>(null);
	const menu = useRef<HTMLDivElement>(null);

	// One entry per step (a withdrawal is open to both sides: one row).
	const steps = useMemo(() => {
		const seen = new Map<AcCardTransitionAction, { to: (typeof record)["status"]; remarks: boolean; reason: boolean }>();
		for (const e of allowedTransitions(record.status)) {
			const cur = seen.get(e.action);
			seen.set(e.action, { to: e.to, remarks: !!e.remarks_required || !!cur?.remarks, reason: !!e.reason_required || !!cur?.reason });
		}
		return [...seen].map(([action, v]) => ({ action, ...v }));
	}, [record.status]);
	const anyEnabled = !!actions?.set_status || steps.some((s) => actions?.transitions[s.action]);

	useEffect(() => {
		if (!open) return;
		menu.current?.querySelector<HTMLElement>("[role=menuitem]:not([aria-disabled=true])")?.focus();
		const onDown = (e: MouseEvent) => {
			if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, [open]);
	const onKey = (e: KeyboardEvent) => {
		if (e.key === "Escape") {
			e.stopPropagation();
			setOpen(false);
			trigger.current?.focus();
			return;
		}
		if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
		e.preventDefault();
		const items = [...(menu.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [])];
		const i = items.indexOf(document.activeElement as HTMLElement);
		items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
	};

	return (
		<div ref={root} className="min-[45rem]:relative" onKeyDown={onKey}>
			<button
				ref={trigger}
				type="button"
				data-ac-status={record.status}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={t("record.statusMenu")}
				onClick={() => setOpen((o) => !o)}
				disabled={busy}
				className="inline-flex h-[2.25rem] cursor-pointer items-center gap-[0.5rem] rounded-full bg-[var(--ac-bg)] pr-[0.625rem] pl-[0.875rem] text-[0.8125rem] font-semibold text-[var(--ac-ink)] shadow-[inset_0_0_0_1px_rgba(18,20,63,0.08)] outline-none hover:brightness-[0.97] focus-visible:ring-2 focus-visible:ring-gms-navy/40 disabled:opacity-60"
			>
				<span aria-hidden className="ac-dot size-[0.5rem]" />
				{t(`record.status.${record.status}`)}
				{anyEnabled || !actions ? (
					<ChevronDownIcon className={`size-[1rem] transition-transform ${open ? "rotate-180" : ""}`} />
				) : (
					<LockIcon className="size-[0.875rem]" />
				)}
			</button>
			{open && (
				<div
					ref={menu}
					role="menu"
					className="absolute top-[calc(100%+0.375rem)] left-1/2 z-30 w-[min(21rem,calc(100vw-2rem))] -translate-x-1/2 rounded-[0.625rem] text-left min-[45rem]:right-0 min-[45rem]:left-auto min-[45rem]:translate-x-0 border border-gms-line bg-base-100 p-[0.375rem] shadow-[0_18px_40px_-20px_rgba(9,10,40,0.45)]"
				>
					<div className="flex items-center gap-[0.625rem] rounded-[0.5rem] px-[0.625rem] py-[0.5rem]" aria-hidden>
						<StatusDot status={record.status} />
						<span className="grid flex-1">
							<b className="text-[0.84375rem] font-semibold text-gms-navy">{t(`record.status.${record.status}`)}</b>
							<small className="text-[0.71875rem] leading-snug text-gms-ink-3">{t(`record.statusHint.${record.status}`)}</small>
						</span>
						<CheckIcon className="size-[1rem] text-gms-navy" />
					</div>
					{steps.length > 0 && <div className="mx-[0.5rem] my-[0.25rem] h-px bg-gms-line" />}
					{steps.length === 0 && !actions?.set_status && <p className="px-[0.625rem] py-[0.5rem] text-[0.75rem] text-gms-ink-3">{t("record.statusLocked")}</p>}
					{steps.map((s) => {
						const enabled = !!actions?.transitions[s.action];
						return (
							<button
								key={s.action}
								type="button"
								role="menuitem"
								aria-disabled={!enabled}
								title={enabled ? undefined : reasonText(actions?.reasons[s.action])}
								onClick={() => {
									if (!enabled) return;
									setOpen(false);
									onPick(s.action);
								}}
								className={`flex w-full items-center gap-[0.625rem] rounded-[0.5rem] px-[0.625rem] py-[0.5rem] text-left outline-none ${
									enabled ? "cursor-pointer hover:bg-gms-soft focus-visible:bg-gms-soft" : "cursor-not-allowed opacity-55 focus-visible:bg-gms-soft"
								}`}
							>
								<StatusDot status={s.to} />
								<span className="grid min-w-0 flex-1">
									<b className="text-[0.84375rem] font-semibold text-gms-navy-ink">
										{s.action === "reopen" || s.action === "unsubmit" ? t(`record.transition.${s.action}`) : t(`record.status.${s.to}`)}
									</b>
									<small className="text-[0.71875rem] leading-snug text-gms-ink-3">
										{enabled ? t(`record.transitionHint.${s.action}`) : reasonText(actions?.reasons[s.action])}
									</small>
								</span>
								{!enabled && <LockIcon className="size-[0.875rem] shrink-0 text-gms-ink-3" />}
								{enabled && (s.remarks || s.reason) && (
									<span className="shrink-0 rounded-[0.25rem] bg-gms-navy-soft px-[0.375rem] py-[0.0625rem] text-[0.65625rem] font-semibold text-gms-navy">
										{s.remarks ? t("record.needsRemarks") : t("record.needsReason")}
									</span>
								)}
							</button>
						);
					})}
					{actions?.set_status && (
						<>
							<div className="mx-[0.5rem] my-[0.25rem] h-px bg-gms-line" />
							<button
								type="button"
								role="menuitem"
								onClick={() => {
									setOpen(false);
									onSetStatus();
								}}
								className="flex w-full cursor-pointer items-center gap-[0.625rem] rounded-[0.5rem] px-[0.625rem] py-[0.5rem] text-left outline-none hover:bg-gms-soft focus-visible:bg-gms-soft"
							>
								<PencilIcon className="size-[0.875rem] shrink-0 text-gms-ink-3" />
								<span className="grid min-w-0 flex-1">
									<b className="text-[0.84375rem] font-semibold text-gms-navy-ink">{t("record.setStatus.menu")}</b>
									<small className="text-[0.71875rem] leading-snug text-gms-ink-3">{t("record.setStatus.menuHint")}</small>
								</span>
								<span className="shrink-0 rounded-[0.25rem] bg-gms-navy-soft px-[0.375rem] py-[0.0625rem] text-[0.65625rem] font-semibold text-gms-navy">
									{t("record.needsReason")}
								</span>
							</button>
						</>
					)}
				</div>
			)}
		</div>
	);
}
